/**
 * Carousel click-and-drag — Pointer Events + setPointerCapture (Yves-style).
 * Document capture delegation so drag works from any card area and late-mounted carousels.
 */
(function () {
  const SELECTOR = 'scroll-carousel[allow-drag]';
  const DRAG_THRESHOLD_PX = 4;
  const finePointerQuery = window.matchMedia('(pointer: fine)');

  function closestCarouselIndex(carousel) {
    const cells = carousel.cells;
    if (!cells?.length) {
      return 0;
    }

    const cellAlign = carousel.cellAlign;
    let targetPoint;

    if (cellAlign === 'center') {
      targetPoint = Math.round(carousel.scrollLeft + carousel.clientWidth / 2);
      let best = 0;
      let bestDist = Infinity;
      cells.forEach((cell, index) => {
        const point = Math.round(cell.offsetLeft + cell.clientWidth / 2);
        const dist = Math.abs(point - targetPoint);
        if (dist < bestDist) {
          bestDist = dist;
          best = index;
        }
      });
      return best;
    }

    if (
      (cellAlign === 'start' && document.dir === 'ltr') ||
      (cellAlign === 'end' && document.dir === 'rtl')
    ) {
      targetPoint = carousel.scrollLeft;
      let best = 0;
      let bestDist = Infinity;
      cells.forEach((cell, index) => {
        const dist = Math.abs(cell.offsetLeft - targetPoint);
        if (dist < bestDist) {
          bestDist = dist;
          best = index;
        }
      });
      return best;
    }

    targetPoint = carousel.scrollLeft + carousel.clientWidth;
    let best = 0;
    let bestDist = Infinity;
    cells.forEach((cell, index) => {
      const point = cell.offsetLeft + cell.clientWidth;
      const dist = Math.abs(point - targetPoint);
      if (dist < bestDist) {
        bestDist = dist;
        best = index;
      }
    });
    return best;
  }

  function isWdScrollCarousel(carousel) {
    return Boolean(carousel.closest('.wd-scroll-carousel'));
  }

  /** Hold scroll position — cancels momentum and avoids theme select() re-align jumps. */
  function stabilizeCarouselScroll(carousel) {
    const left = carousel.scrollLeft;
    carousel.scrollTo({ left, top: carousel.scrollTop, behavior: 'auto' });
  }

  function bindTouchScrollStabilize(carousel) {
    if (!isWdScrollCarousel(carousel) || carousel.dataset.wdTouchStabilize === 'true') {
      return;
    }
    carousel.dataset.wdTouchStabilize = 'true';

    carousel.addEventListener(
      'touchend',
      () => {
        if (carousel.classList.contains('is-wd-carousel-dragging')) {
          return;
        }
        requestAnimationFrame(() => {
          requestAnimationFrame(() => stabilizeCarouselScroll(carousel));
        });
      },
      { passive: true }
    );
  }

  function initTouchStabilize(root) {
    (root || document)
      .querySelectorAll('.wd-scroll-carousel scroll-carousel[allow-drag]')
      .forEach(bindTouchScrollStabilize);
  }

  function setCellSnapDuringDrag(carousel, enabled) {
    carousel.querySelectorAll(':scope > .wd-fbt-card, :scope > .product-card').forEach((cell) => {
      if (enabled) {
        cell.dataset.wdPrevSnapAlign = cell.style.scrollSnapAlign || '';
        cell.style.scrollSnapAlign = 'none';
      } else if ('wdPrevSnapAlign' in cell.dataset) {
        if (cell.dataset.wdPrevSnapAlign) {
          cell.style.scrollSnapAlign = cell.dataset.wdPrevSnapAlign;
        } else {
          cell.style.removeProperty('scroll-snap-align');
        }
        delete cell.dataset.wdPrevSnapAlign;
      }
    });
  }

  function isCarouselInteractiveTarget(target) {
    return Boolean(
      target?.closest?.(
        'button, input, select, textarea, label, product-form, form, quick-buy-modal, .product-card__quick-add-form, [role="button"]'
      )
    );
  }

  function handleCarouselPointerDown(event, carousel) {
    if (isCarouselInteractiveTarget(event.target)) {
      return;
    }

    if (!finePointerQuery.matches) {
      return;
    }

    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    const cells = carousel.cells;
    if (!cells?.length || cells.length <= 1) {
      return;
    }

    if (carousel.scrollWidth - carousel.clientWidth <= 1) {
      return;
    }

    const pendingLink =
      !isCarouselInteractiveTarget(event.target) && event.target.closest?.('a[href]');

    if (pendingLink && carousel.contains(pendingLink)) {
      event.preventDefault();
    }

    const start = {
      x: event.clientX,
      y: event.clientY,
      scrollLeft: carousel.scrollLeft,
      scrollTop: carousel.scrollTop,
    };

    let isDragging = false;
    const pointerId = event.pointerId;

    try {
      carousel.setPointerCapture(pointerId);
    } catch (_) {
      return;
    }

    const onPointerMove = (moveEvent) => {
      if (moveEvent.pointerId !== pointerId) {
        return;
      }

      const dx = moveEvent.clientX - start.x;
      const dy = moveEvent.clientY - start.y;

      if (!isDragging) {
        if (Math.abs(dx) <= DRAG_THRESHOLD_PX && Math.abs(dy) <= DRAG_THRESHOLD_PX) {
          return;
        }
        isDragging = true;
        carousel.classList.add('is-wd-carousel-dragging');
        carousel.style.scrollSnapType = 'none';
        carousel.style.scrollBehavior = 'auto';
        setCellSnapDuringDrag(carousel, true);
      }

      moveEvent.preventDefault();
      carousel.scrollTo({
        left: start.scrollLeft - dx,
        top: start.scrollTop - dy,
        behavior: 'auto',
      });
    };

    const onClickCapture = (clickEvent) => {
      if (
        isDragging &&
        (Math.abs(clickEvent.clientX - start.x) > DRAG_THRESHOLD_PX ||
          Math.abs(clickEvent.clientY - start.y) > DRAG_THRESHOLD_PX)
      ) {
        clickEvent.preventDefault();
        clickEvent.stopImmediatePropagation();
      }
    };

    const endDrag = (endEvent) => {
      if (endEvent.pointerId !== pointerId) {
        return;
      }

      carousel.removeEventListener('pointermove', onPointerMove);
      carousel.removeEventListener('pointerup', endDrag);
      carousel.removeEventListener('pointercancel', endDrag);

      try {
        carousel.releasePointerCapture(pointerId);
      } catch (_) {}

      carousel.classList.remove('is-wd-carousel-dragging');
      setCellSnapDuringDrag(carousel, false);

      if (!isDragging) {
        carousel.style.removeProperty('scroll-snap-type');
        carousel.style.removeProperty('scroll-behavior');
        if (
          pendingLink &&
          endEvent.button === 0 &&
          !endEvent.ctrlKey &&
          !endEvent.metaKey &&
          !endEvent.shiftKey &&
          !endEvent.altKey
        ) {
          pendingLink.click();
        }
        return;
      }

      carousel.style.removeProperty('scroll-snap-type');
      carousel.style.removeProperty('scroll-behavior');

      const dragged =
        Math.abs(carousel.scrollLeft - start.scrollLeft) > DRAG_THRESHOLD_PX ||
        Math.abs(endEvent.clientX - start.x) > DRAG_THRESHOLD_PX;

      if (dragged && isWdScrollCarousel(carousel)) {
        // Homepage carousels: keep release position; theme select() would jump to cell-align snap.
        stabilizeCarouselScroll(carousel);
        return;
      }

      if (dragged && typeof carousel.select === 'function') {
        carousel.select(closestCarouselIndex(carousel), { instant: true });
      }
    };

    carousel.addEventListener('pointermove', onPointerMove);
    carousel.addEventListener('pointerup', endDrag);
    carousel.addEventListener('pointercancel', endDrag);
    carousel.addEventListener('click', onClickCapture, { capture: true, once: true });
  }

  document.addEventListener(
    'pointerdown',
    (event) => {
      const carousel = event.target.closest(SELECTOR);
      if (!carousel) {
        return;
      }
      handleCarouselPointerDown(event, carousel);
    },
    true
  );

  document.addEventListener(
    'dragstart',
    (event) => {
      if (event.target.closest(SELECTOR)) {
        event.preventDefault();
      }
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initTouchStabilize());
  } else {
    initTouchStabilize();
  }

  document.addEventListener('shopify:section:load', (event) => initTouchStabilize(event.target));
})();
